import {
  InteractionStatus,
  InteractionType,
} from "@azure/msal-browser";
import {
  MsalAuthenticationTemplate,
  MsalProvider,
  useAccount,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { loginRequest } from "./msalConfig";
import {
  authClient,
  buildUserProfile,
  ensureMsalInitialized,
  extractRoles,
  getAccessToken,
  hasRole,
  hasRoles,
  msalInstance,
  updateAuthClient,
} from "./msalInstance";
import { AuthContextType, AuthUserProfile } from "./types";

export const AuthContext = createContext<AuthContextType>(authClient);

const AuthProviderInner: FC<{ children: ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || {});

  const activeAccount = useMemo(() => {
    return instance.getActiveAccount() || account || accounts[0] || null;
  }, [instance, account, accounts]);

  const syncToken = useCallback(async () => {
    if (activeAccount) {
      updateAuthClient(activeAccount);
    } else {
      updateAuthClient(null);
    }
  }, [activeAccount]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      if (!instance.getActiveAccount() && accounts.length > 0) {
        instance.setActiveAccount(accounts[0]);
      }
    }
  }, [inProgress, instance, accounts, syncToken]);

  const loadUserProfile = useCallback(async (): Promise<AuthUserProfile> => {
    return buildUserProfile(activeAccount);
  }, [activeAccount]);

  const login = useCallback(async () => {
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(async () => {
    await instance.logoutRedirect({
      account: activeAccount || undefined,
      postLogoutRedirectUri: window.location.origin,
    });
  }, [instance, activeAccount]);

  const authValue: AuthContextType = useMemo(() => {
    const tokenParsed = authClient.tokenParsed;
    const context: AuthContextType = {
      authenticated: isAuthenticated || !!activeAccount,
      initialized: true,
      user: activeAccount ? buildUserProfile(activeAccount) : null,
      account: activeAccount,
      idToken: activeAccount?.idToken || authClient.idToken,
      tokenParsed,
      idTokenParsed: tokenParsed,
      hasRealmRole: (role: string) => hasRole(activeAccount, role),
      hasResourceRole: (role: string) => hasRole(activeAccount, role),
      hasRoles: () => hasRoles(activeAccount),
      roles: extractRoles(activeAccount),
      loadUserProfile,
      login,
      logout,
      getToken: () => getAccessToken(),
      get auth() {
        return context;
      },
    };
    return context;
  }, [
    isAuthenticated,
    activeAccount,
    loadUserProfile,
    login,
    logout,
  ]);

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    ensureMsalInitialized().then(() => {
      setIsInitialized(true);
    });
  }, []);

  if (!isInitialized) {
    return null;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={loginRequest}
      >
        <AuthProviderInner>{children}</AuthProviderInner>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
};
